import type { TourDefinition, TourStep } from "@/config/tours";

/** Shared look for every step: no icon, a soft rounded spotlight. */
function step(
  title: string,
  content: string,
  selector: string,
  side: TourStep["side"],
): TourStep {
  return {
    icon: null,
    title,
    content,
    selector,
    side,
    pointerPadding: 8,
    pointerRadius: 8,
  };
}

// Checkout (billing/check-out) anchors, shared by the three checkout tours.
const CHECKOUT_SEARCH = "#tour-train-checkout-search";
const CHECKOUT_TABS = "#tour-train-checkout-tabs";
const CHECKOUT_CATALOG = "#tour-train-checkout-catalog";
const CHECKOUT_TICKET = "#tour-train-checkout-ticket";
const CHECKOUT_PAY = "#tour-train-checkout-pay";

export const billingAdminTrainingTours: TourDefinition[] = [
  {
    tour: "training-checkout",
    steps: [
      step(
        "Find a service",
        "Type the name of a service here, or pick a tab like Services or Products to browse.",
        CHECKOUT_SEARCH,
        "bottom",
      ),
      step(
        "Add it to the cart",
        "Click a service to add it, then add a product the same way. Today's booked services also show up here.",
        CHECKOUT_CATALOG,
        "right",
      ),
      step(
        "Check the ticket",
        "Everything you add lands on the ticket. Use the + and − buttons to change the amount, or Edit to change the price.",
        CHECKOUT_TICKET,
        "left",
      ),
      step(
        "Go to payment",
        "Click Continue to Payment, then pick Cash, or Card and type the test card 4242 4242 4242 4242 with any future date.",
        CHECKOUT_PAY,
        "top",
      ),
    ],
  },
  {
    tour: "training-sell-membership",
    steps: [
      step(
        "Open memberships",
        "Click the Memberships tab, or Packages for a bundle of visits. You can also type its name in the search box.",
        CHECKOUT_TABS,
        "bottom",
      ),
      step(
        "Pick one",
        "Click a membership to open its sale window. Choose the start date and who sold it, then add it to the ticket.",
        CHECKOUT_CATALOG,
        "right",
      ),
      step(
        "Check the ticket",
        "The membership or package now shows on the ticket with today's price.",
        CHECKOUT_TICKET,
        "left",
      ),
      step(
        "Take the payment",
        "Click Continue to Payment and pay with Cash or Card (test card 4242 4242 4242 4242). The membership starts once it's paid.",
        CHECKOUT_PAY,
        "top",
      ),
    ],
  },
  {
    tour: "training-discount",
    steps: [
      step(
        "Add an item first",
        "Search for a service or product and click it to put it on the ticket.",
        CHECKOUT_SEARCH,
        "bottom",
      ),
      step(
        "Use a benefit",
        "If the patient has a membership or package, its benefits show here. Click one to apply it to the cart.",
        CHECKOUT_CATALOG,
        "right",
      ),
      step(
        "Discount one line",
        "On the ticket, click Edit on a line to add a discount to just that item. Pick a reason so the clinic knows why.",
        CHECKOUT_TICKET,
        "left",
      ),
      step(
        "Or discount the whole order",
        "Under the ticket lines, click Add discount or promotion to take money off everything. Then click Continue to Payment.",
        CHECKOUT_PAY,
        "top",
      ),
    ],
  },
  {
    tour: "training-refund",
    steps: [
      step(
        "Find the payment",
        "Search or filter by patient name or date to find the payment you want to refund.",
        "#tour-train-payments-filters",
        "bottom",
      ),
      step(
        "Open its menu",
        "Click the arrow on a row to see its history. To refund, click ⋯ at the end of the row and choose Refund Payment.",
        "#tour-train-payments-table thead",
        "bottom",
      ),
      step(
        "Enter the amount and reason",
        "Choose an amount or the items to refund, where the money goes back to, and a reason. Then confirm the refund.",
        "#tour-train-payments-table thead",
        "bottom",
      ),
    ],
  },
  {
    tour: "training-outstanding-charges",
    steps: [
      step(
        "Show only unpaid charges",
        "Add a Status filter and pick Outstanding to see charges that still have money owing.",
        "#tour-train-charges-filters",
        "bottom",
      ),
      step(
        "Find the charge",
        "Each charge shows how much is still outstanding. Click the arrow on a row to see what was paid so far.",
        "#tour-train-charges-table thead",
        "bottom",
      ),
      step(
        "Take the rest",
        "Click ⋯ at the end of the row and choose Take Payment. Checkout opens with the balance ready to pay.",
        "#tour-train-charges-table thead",
        "bottom",
      ),
    ],
  },
  {
    tour: "training-close-register",
    steps: [
      step(
        "What should be in the drawer",
        "Expected Cash Balance starts from the last closing count and adds the cash taken and given back since.",
        "#tour-train-close-register-expected",
        "right",
      ),
      step(
        "Count your cash",
        "Enter how many of each bill and coin are in the drawer. The totals add up as you type.",
        "#tour-train-close-register-count",
        "bottom",
      ),
      step(
        "Check the difference",
        "Discrepancy shows whether the drawer is over or short. If it is off, look for a cash payment or refund that was missed or entered wrong.",
        "#tour-train-close-register-summary",
        "right",
      ),
      step(
        "Close the register",
        "Click Close Register to save the count. Tomorrow's expected balance starts from it.",
        "#tour-train-close-register-submit",
        "bottom",
      ),
    ],
  },
  {
    tour: "training-ask-decoda",
    steps: [
      step(
        "Meet Ask Decoda",
        "Ask questions about your clinic in plain words, like you would ask a coworker.",
        "#tour-train-ask-decoda h3",
        "bottom",
      ),
      step(
        "Type a question",
        'Try "How many appointments do we have tomorrow?" and press Enter to send.',
        '#tour-train-ask-decoda [contenteditable="true"]',
        "bottom",
      ),
      step(
        "Your past chats",
        "Answers show in the middle of the screen and can link to the right screen. Every chat is kept in this list so you can come back to it.",
        "#tour-train-ask-decoda h2",
        "right",
      ),
    ],
  },
  {
    tour: "training-analytics",
    steps: [
      step(
        "Your reports",
        "Every dashboard and report you can open is listed on this screen.",
        "#tour-train-analytics-header",
        "bottom",
      ),
      step(
        "Pick a dashboard",
        "Click any dashboard to open it. They are grouped by topic, like billing or scheduling. Once it's open, use Date Range at the top to change the period, and click a number or chart to see the records behind it.",
        "#tour-train-analytics-first-group",
        "bottom",
      ),
    ],
  },
  {
    tour: "training-staff-pay",
    steps: [
      step(
        "Pick a pay period",
        "Choose the dates you are paying for, like last week or last month.",
        "#tour-train-staff-pay-range",
        "bottom",
      ),
      step(
        "See each person's pay",
        "Each row shows commissions, tips, hourly pay and the total for one staff member.",
        "#tour-train-staff-pay-table thead",
        "bottom",
      ),
      step(
        "See how it was worked out",
        "Click a person's row to open the details: each sale, tip and shift that adds up to their pay.",
        "#tour-train-staff-pay-table thead",
        "bottom",
      ),
    ],
  },
  {
    tour: "training-inventory-count",
    steps: [
      step(
        "Take inventory",
        "Use this screen to count what's on your shelves and fix the numbers in the system.",
        "#tour-train-stock-count-header",
        "bottom",
      ),
      step(
        "Choose a location",
        "Count everything, or pick one location to count.",
        "#tour-train-stock-count-location",
        "right",
      ),
      step(
        "Start, count and submit",
        "Click Start counting. Type a default reason, enter what you find for a few items, then click Submit count.",
        "[data-testid='start-count']",
        "right",
      ),
    ],
  },
  {
    tour: "training-edit-service",
    steps: [
      step(
        "Your services",
        "Every service your clinic offers lives here, with its price and length.",
        "#tour-train-services-header",
        "bottom",
      ),
      step(
        "Find a service",
        "Search or filter to find the service you want to change.",
        "#tour-train-services-filters",
        "bottom",
      ),
      step(
        "Open it",
        "Click ⋯ at the end of the row and choose Edit to open the service.",
        "#tour-train-services-table thead",
        "bottom",
      ),
      step(
        "Change and save",
        "Change the price under Pricing & Payments or the length under Scheduling & Booking, then click Update Service.",
        "#tour-train-services-table thead",
        "bottom",
      ),
    ],
  },
  {
    tour: "training-invite",
    steps: [
      step(
        "Your team",
        "Everyone who can sign in to Decoda is listed here, with people still to accept their invite at the bottom.",
        "#tour-train-users-header",
        "bottom",
      ),
      step(
        "Invite someone",
        "Click Invite User to add a new team member.",
        "#invite-user",
        "left",
      ),
      step(
        "Fill in their details",
        "Enter their first name, last name and email, and pick a role. The role decides what they can see and do. Then click Invite user.",
        "#invite-user",
        "left",
      ),
    ],
  },
  {
    tour: "training-setup-guide",
    steps: [
      step(
        "Your setup guide",
        "Everything to set up before and after go-live, in one list.",
        "#tour-train-setup-guide-header",
        "bottom",
      ),
      step(
        "Open a section",
        "Each section groups related setup. Click one to see its items, and use the filters to show only required ones.",
        "#tour-train-setup-guide-list",
        "right",
      ),
      step(
        "Work through an item",
        "Click an item to read what to do. Open takes you to the right screen, and you can tick it off when it's done.",
        "#tour-train-setup-guide-list",
        "right",
      ),
      step(
        "Track your progress",
        "See how many sections are done and what's still blocking go-live.",
        "#tour-train-setup-guide-progress",
        "left",
      ),
    ],
  },
];
